import * as Sentry from '@sentry/nextjs';
import { Elements } from '@stripe/react-stripe-js';
import Button from 'components/buttons/button';
import { CoinButton } from 'components/buttons/payments';
import StripeError from 'components/payments/PaymentError';
import { useAnalytics } from 'hooks/useAnalytics';
import useMediaQuery from 'hooks/useMediaQuery';
import coinIcon from 'images/icons/coin.svg';
import { convertDonationToCoinValue } from 'lib/api/premium';
import getStripe from 'lib/getStripe';
import {
  getStripeAppearanceConfig,
  getStripeFontConfig,
  RECURRING_PERIODS,
} from 'lib/stripe';
import Img from 'next/image';
import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import {
  BankAccountPropType,
  CardPaymentMethodPropType,
  CashAppPropType,
  LinkPropType,
  PricesPropType,
} from 'utilities/propTypes';
import { calculateCosts } from 'utilities/stripe/calculations';
import { normalizePaymentMethod } from 'utilities/stripe/paymentMethod';
import { ExistingPaymentMethodDisplay } from './PaymentMethodDisplay';
import SetupForm from './SetupForm';

const stripePromise = getStripe();

const EXTRA_COINS_CENTS = 500;

const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

const SubscriptionForm = ({
  customer,
  paymentMethod,
  prices,
  clientSecret = null,
}) => {
  const { donationTrackEvent } = useAnalytics();
  const router = useRouter();
  const [recurringPeriod, setRecurringPeriod] = useState(
    RECURRING_PERIODS.MONTH
  );
  const [extraCoins, setExtraCoins] = useState(false);
  const [tip, setTip] = useState(0);
  const [recurringCostCents, setRecurringCostCents] = useState(
    prices.month.unitAmount
  );
  const [oneTimeCostCents, setOneTimeCostCents] = useState(0);
  const [canSubmit, setCanSubmit] = useState(false);
  const [failedPayment, setFailedPayment] = useState(false);
  const [validPaymentMethod, setValidPaymentMethod] = useState(paymentMethod);

  const handlePlanOptionsChange = (e) => {
    const value =
      e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    switch (e.target.name) {
      case 'extraCoins':
        setExtraCoins(value);
        break;
      case 'tip':
        setTip(value);
        break;
      default:
        break;
    }
  };

  const finalizePremiumPurchase = async () => {
    setCanSubmit(false);

    const requestBody = {
      uuid: customer.uuid,
      customerId: customer.id,
      priceId: prices[recurringPeriod].id,
      extraCoinsCents: extraCoins ? EXTRA_COINS_CENTS : 0,
      // Should submit as number but the API accepts string
      tipPercent: tip,
    };
    try {
      // Some of this logic is duplicated in BuyCoins.js
      const res = await fetch('/api/payments/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': 'TODO',
        },
        body: JSON.stringify(requestBody),
      });
      // Parse the result
      const data = await res.json();
      Sentry.addBreadcrumb({
        category: 'stripe:subscribe',
        message: 'Subscription creation result',
        data: {
          responseData: data,
          requestBody: requestBody,
        },
      });

      if (data.error) {
        const eventId = Sentry.captureException(
          data.error instanceof Object ? data.error : new Error(data.error)
        );
        // data.error is the sanitized card-error shape from the API
        // (message/type/code/decline_code/doc_url) when Stripe declines
        // the card, or a plain string for other failures.
        setFailedPayment({
          error:
            typeof data.error === 'string'
              ? { message: data.error }
              : data.error,
          eventId,
        });
        return;
      }

      donationTrackEvent({
        recurringPeriod: RECURRING_PERIODS[recurringPeriod],
        donationValueCents: recurringCostCents,
      });
      // Track one time coins
      if (oneTimeCostCents) {
        donationTrackEvent({
          donationValueCents: oneTimeCostCents,
        });
      }

      // If the user has a payment method, we can use it to create the subscription
      router.push({
        pathname: '/premium/thanks',
        query: { pi: data.paymentIntent.id },
      });
    } catch (error) {
      const eventId = Sentry.captureException(error);
      setFailedPayment({ error, eventId });
    }
  };

  // This logic is duplicated in BuyCoins.js
  const onPaymentMethodAdded = async (payload) => {
    fetch('/api/payments/set-payment-method-default', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        stripeCustomerId: customer.id,
        paymentMethodId: payload.paymentMethodId,
        uuid: customer.uuid,
      }),
    })
      .then((res) => res.json())
      .then((data) => {
        Sentry.addBreadcrumb({
          category: 'stripe:set-payment-method-default',
          message: 'Default payment method set via api',
          data: data,
        });

        const normalizedPaymentMethod = normalizePaymentMethod({
          defaultPaymentMethod: data.payment_method,
        });
        setValidPaymentMethod(normalizedPaymentMethod);
      });
  };

  // Checks to see if the form is ready to be submitted
  useEffect(() => {
    if (
      (recurringPeriod === RECURRING_PERIODS.MONTH ||
        recurringPeriod === RECURRING_PERIODS.YEAR) &&
      validPaymentMethod
    ) {
      setCanSubmit(true);
    }
  }, [recurringPeriod, validPaymentMethod]);

  // Track the current one-time and recurring costs
  useEffect(() => {
    const { recurringCost, oneTimeTotalCost } = calculateCosts({
      recurringPriceCents: prices[recurringPeriod].unitAmount,
      lineItems: [{ amountCents: extraCoins ? EXTRA_COINS_CENTS : 0 }],
      tipPercent: tip,
    });

    setRecurringCostCents(recurringCost);
    setOneTimeCostCents(oneTimeTotalCost);
  }, [prices, recurringPeriod, extraCoins, tip]);

  // Get stripe form apprearance config
  const prefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const appearanceConfig = getStripeAppearanceConfig({ darkMode: prefersDark });
  const fontConfig = getStripeFontConfig();

  return (
    <>
      <div>
        <div className="grid grid-cols-2 gap-2">
          <CoinButton
            id={'monthly'}
            selected={recurringPeriod === RECURRING_PERIODS.MONTH}
            handleClick={() => {
              setRecurringPeriod(RECURRING_PERIODS.MONTH);
            }}
          >
            <Img src={coinIcon} alt="" height="24" width="24" />
            <div className="text-left leading-tight">
              <h5 className="font-semibold">
                ${prices.month.formattedAmount}{' '}
                <span className="font-normal sm:hidden">/ mo</span>
                <span className="hidden font-normal sm:inline">/ month</span>
              </h5>
              <h6 className="text-sm font-normal text-stone-500">
                {convertDonationToCoinValue({
                  donation_cents: prices.month.unitAmount,
                  isPremium: true,
                })}{' '}
                coins
              </h6>
            </div>
          </CoinButton>
          <CoinButton
            id={'monthly'}
            selected={recurringPeriod === 'year'}
            handleClick={() => {
              setRecurringPeriod(RECURRING_PERIODS.YEAR);
            }}
          >
            <Img src={coinIcon} alt="" height="24" width="24" />
            <div className="text-left leading-tight">
              <h5 className="font-semibold">
                ${prices.year.formattedAmount}{' '}
                <span className="font-normal sm:hidden">/ yr</span>
                <span className="hidden font-normal sm:inline">/ year</span>
              </h5>
              <h6 className="text-sm font-normal text-stone-500">
                {convertDonationToCoinValue({
                  donation_cents: prices.year.unitAmount,
                  isPremium: true,
                }).toLocaleString()}{' '}
                coins
              </h6>
            </div>
          </CoinButton>
        </div>
        <div className="my-4 flex flex-wrap items-center gap-3">
          <h2>↳ Add Coins</h2>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              name="extraCoins"
              checked={extraCoins}
              onChange={handlePlanOptionsChange}
            />
            ${EXTRA_COINS_CENTS / 100} for{' '}
            {convertDonationToCoinValue({
              donation_cents: EXTRA_COINS_CENTS,
              isPremium: true,
            })}{' '}
            extra coins
          </label>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="">↳ Tip the Fund</h2>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="tip"
              value={10}
              checked={tip === '10'}
              onChange={handlePlanOptionsChange}
            />{' '}
            10%
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="tip"
              value={20}
              checked={tip === '20'}
              onChange={handlePlanOptionsChange}
            />{' '}
            20%
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="tip"
              value={30}
              checked={tip === '30'}
              onChange={handlePlanOptionsChange}
            />{' '}
            30%
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="tip"
              value={0}
              checked={tip === '0'}
              onChange={handlePlanOptionsChange}
            />{' '}
            No thanks!
          </label>
        </div>
      </div>

      <div className="mt-2">
        {!validPaymentMethod
          ? clientSecret && (
              <Elements
                stripe={stripePromise}
                options={{
                  clientSecret,
                  appearance: appearanceConfig,
                  fonts: fontConfig,
                }}
              >
                <SetupForm onComplete={onPaymentMethodAdded} />
              </Elements>
            )
          : ExistingPaymentMethodDisplay({
              paymentMethod: validPaymentMethod,
              billingPortalSession: customer.billingPortalSession,
            })}
      </div>

      <div className="mt-2">
        {failedPayment ? (
          <StripeError
            error={failedPayment.error}
            sentryEventId={failedPayment.eventId}
          />
        ) : (
          <Button
            theme="blue"
            onClick={finalizePremiumPurchase}
            disabled={!canSubmit}
            preventDefault
          >
            Pay {formatter.format(oneTimeCostCents / 100)} today and{' '}
            {formatter.format(recurringCostCents / 100)}{' '}
            {recurringPeriod === RECURRING_PERIODS.MONTH ? 'monthly' : 'yearly'}
          </Button>
        )}
      </div>
    </>
  );
};

SubscriptionForm.propTypes = {
  prices: PricesPropType,
  customer: PropTypes.shape({
    id: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    uuid: PropTypes.string.isRequired,
    subscription: PropTypes.shape({
      id: PropTypes.string.isRequired,
      active: PropTypes.bool.isRequired,
      cancelAt: PropTypes.number,
      currentPeriodEnd: PropTypes.number,
      plan: PropTypes.shape({
        id: PropTypes.string.isRequired,
        amount: PropTypes.number.isRequired,
        amountFormatted: PropTypes.string.isRequired,
        interval: PropTypes.oneOf([
          RECURRING_PERIODS.MONTH,
          RECURRING_PERIODS.YEAR,
        ]).isRequired,
      }).isRequired,
    }),
    billingPortalSession: PropTypes.string,
  }),
  paymentMethod: PropTypes.oneOfType([
    CardPaymentMethodPropType,
    BankAccountPropType,
    LinkPropType,
    CashAppPropType,
  ]),
  clientSecret: PropTypes.string,
};

export default SubscriptionForm;
